<script setup>
import { ref, watch, computed } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import FilterSelect from '../../socialMonitoring/components/FilterSelect.vue';
import { PIN_TYPES, AREA_CATEGORIES } from '../utils/pinMeta';

const props = defineProps({
  open: { type: Boolean, default: false },
  pin: { type: Object, default: null },
  coord: { type: Object, default: null },
  saving: { type: Boolean, default: false },
});
const emit = defineEmits(['close', 'save', 'delete']);

const form = ref({
  title: '',
  description: '',
  pinType: 'grupo',
  category: '',
  lat: 0,
  lng: 0,
});
const isEdit = computed(
  () => props.pin && !props.pin.virtual && typeof props.pin.id === 'number'
);
const isVirtual = computed(() => props.pin && props.pin.virtual);

const typeOptions = PIN_TYPES.map(p => ({
  value: p.value,
  label: p.label,
  icon: p.icon,
}));
const categoryOptions = computed(() => {
  if (form.value.pinType === 'area') {
    return [
      { value: '', label: 'Sem categoria' },
      ...AREA_CATEGORIES.map(c => ({ value: c.value, label: c.label })),
    ];
  }
  return [];
});

watch(
  () => [props.open, props.pin, props.coord],
  () => {
    if (!props.open) return;
    if (props.pin) {
      form.value = {
        title: props.pin.title,
        description: props.pin.description || '',
        pinType: props.pin.pinType,
        category: props.pin.category || '',
        lat: props.pin.lat,
        lng: props.pin.lng,
      };
    } else if (props.coord) {
      form.value = {
        title: '',
        description: '',
        pinType: 'grupo',
        category: '',
        lat: props.coord.lat,
        lng: props.coord.lng,
      };
    }
  },
  { immediate: true }
);

function submit() {
  const payload = { ...form.value };
  if (!payload.category) delete payload.category;
  if (isEdit.value) payload.id = props.pin.id;
  emit('save', payload);
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="open"
      class="fixed inset-0 z-50 flex items-center justify-center p-4"
      @click.self="emit('close')"
    >
      <div
        class="absolute inset-0 bg-black/50 backdrop-blur-sm"
        @click="emit('close')"
      />
      <div
        class="relative z-10 w-full max-w-lg rounded-xl bg-n-background shadow-2xl"
      >
        <div
          class="flex items-center justify-between border-b border-n-weak px-5 py-3"
        >
          <h3 class="text-base font-bold text-n-slate-12">
            {{
              isEdit ? 'Editar pin' : isVirtual ? 'Pin automático' : 'Novo pin'
            }}
          </h3>
          <Button
            variant="ghost"
            size="sm"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>

        <div v-if="isVirtual" class="p-5 text-sm text-n-slate-11">
          <p class="mb-2">
            Este pin é <strong>automático</strong> — vem de um
            {{
              pin.relatedType === 'Worker' ? 'cabo eleitoral' : pin.relatedType
            }}
            com coordenadas geográficas.
          </p>
          <div class="space-y-1 rounded-md bg-n-slate-2 p-3 text-xs">
            <p>
              <strong>{{ pin.title }}</strong>
            </p>
            <p>{{ pin.description }}</p>
            <p class="text-n-slate-11">
              Lat {{ pin.lat.toFixed(5) }} · Lng {{ pin.lng.toFixed(5) }}
            </p>
          </div>
          <p class="mt-3 text-xs">
            Edite direto na aba "Campo" do cabo correspondente.
          </p>
        </div>

        <div v-else class="space-y-3 p-5">
          <div>
            <label class="mb-1 block text-xs font-semibold text-n-slate-11">Tipo</label>
            <FilterSelect
              v-model="form.pinType"
              :options="typeOptions"
              min-width="100%"
            />
          </div>
          <div v-if="categoryOptions.length">
            <label class="mb-1 block text-xs font-semibold text-n-slate-11">Categoria</label>
            <FilterSelect
              v-model="form.category"
              :options="categoryOptions"
              min-width="100%"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-semibold text-n-slate-11">Título</label>
            <input
              v-model="form.title"
              type="text"
              class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
            />
          </div>
          <div>
            <label class="mb-1 block text-xs font-semibold text-n-slate-11">Descrição</label>
            <textarea
              v-model="form.description"
              rows="2"
              class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
            />
          </div>
          <div class="grid grid-cols-2 gap-2">
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Latitude</label>
              <input
                v-model.number="form.lat"
                type="number"
                step="0.000001"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label class="mb-1 block text-xs font-semibold text-n-slate-11">Longitude</label>
              <input
                v-model.number="form.lng"
                type="number"
                step="0.000001"
                class="w-full rounded-lg border border-n-strong/40 bg-n-background px-3 py-2 text-sm"
              />
            </div>
          </div>
        </div>

        <div
          class="flex items-center justify-between border-t border-n-weak px-5 py-3"
        >
          <Button
            v-if="isEdit"
            variant="ghost"
            size="sm"
            icon="i-lucide-trash-2"
            color="ruby"
            @click="emit('delete', pin)"
          >
            Excluir
          </Button>
          <span v-else />
          <div class="flex gap-2">
            <Button variant="outline" size="sm" @click="emit('close')">
              Cancelar
            </Button>
            <Button
              v-if="!isVirtual"
              variant="solid"
              color="blue"
              size="sm"
              :is-loading="saving"
              :disabled="!form.title.trim()"
              @click="submit"
            >
              {{ isEdit ? 'Salvar' : 'Criar' }}
            </Button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>
